// components/pagos/DialogFacturasMedioPago.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  TablePagination,
  TableFooter
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import moment from 'moment';
import { UrlApi } from '../../../../../services/apirest';
import { verEnviarPDFenvasado } from '../../../../../services/services';
import { formatNumber, getMedioPagoIcon, getMedioPagoColor } from './mediosPagoHelpers';

const DialogFacturasMedioPago = ({
  open,
  onClose,
  medioPago,
  sucursalId,
  sucursalNombre,
  desde,
  hasta
}) => {
  const [facturas, setFacturas] = useState([]);
  const [loadingFacturas, setLoadingFacturas] = useState(false);
  const [errorFacturas, setErrorFacturas] = useState(null);
  const [facturasExpandidas, setFacturasExpandidas] = useState({});
  const [pagosPorFactura, setPagosPorFactura] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (!open || !medioPago || !sucursalId || !desde || !hasta) return;

    const cargarFacturas = async () => {
      setLoadingFacturas(true);
      setErrorFacturas(null);
      setFacturas([]);
      setFacturasExpandidas({});
      setPagosPorFactura({});
      setPage(0);

      try {
        const esCtaCte = medioPago.medio_pago === 'Cuenta Corriente';
        // Cuenta Corriente son pedidos facturados con estado 312 ("Facturado Cta. Cte."),
        // por eso usa un endpoint propio en vez de facturasxmediopago (que filtra por medio de pago real)
        const url = esCtaCte
          ? `${UrlApi}/envasado_pagos_combinados/facturasctacte/${desde} 00:00:00/${hasta} 23:59:59/${sucursalId}`
          : `${UrlApi}/envasado_pagos_combinados/facturasxmediopago/${encodeURIComponent(medioPago.medio_pago)}/${desde} 00:00:00/${hasta} 23:59:59/${sucursalId}`;

        const res = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.message || `Error ${res.status}: ${res.statusText}`);
        }

        const response = await res.json();

        if (Array.isArray(response)) {
          setFacturas(response);
        } else if (response.success) {
          setFacturas(response.data || []);
        } else {
          setErrorFacturas(response.error || 'Error al cargar las facturas');
        }
      } catch (error) {
        console.error('Error al cargar facturas del medio de pago:', error);
        setErrorFacturas(error.message || 'Error al cargar las facturas');
      } finally {
        setLoadingFacturas(false);
      }
    };

    cargarFacturas();
  }, [open, medioPago, sucursalId, desde, hasta]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const facturasPaginadas = facturas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Expande/colapsa el detalle de pagos de una factura, trayéndolos la primera vez
  const toggleFacturaExpandida = async (factura) => {
    const idFactura = factura.id_factura;

    setFacturasExpandidas(prev => ({
      ...prev,
      [idFactura]: !prev[idFactura]
    }));

    if (pagosPorFactura[idFactura] || !factura.id_pedido) return;

    setPagosPorFactura(prev => ({
      ...prev,
      [idFactura]: { loading: true, error: null, data: [] }
    }));

    try {
      const res = await fetch(`${UrlApi}/envasado_pagos_combinados/xpedido/${factura.id_pedido}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || `Error ${res.status}: ${res.statusText}`);
      }

      const pagos = await res.json();
      setPagosPorFactura(prev => ({
        ...prev,
        [idFactura]: { loading: false, error: null, data: Array.isArray(pagos) ? pagos : [] }
      }));
    } catch (error) {
      console.error('Error al cargar pagos del pedido:', error);
      setPagosPorFactura(prev => ({
        ...prev,
        [idFactura]: { loading: false, error: error.message || 'Error al cargar los pagos', data: [] }
      }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <ReceiptIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Facturas · {medioPago?.medio_pago || 'Sin definir'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {sucursalNombre ? `Sucursal: ${sucursalNombre} · ` : ''}{desde} al {hasta}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loadingFacturas ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress size={32} sx={{ color: '#757575' }} />
          </Box>
        ) : errorFacturas ? (
          <Alert severity="error">{errorFacturas}</Alert>
        ) : facturas.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              No hay facturas asociadas a este medio de pago en el período
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#e8e8e8' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Comprobante</TableCell>
                  <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Cliente</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Pedido</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Total</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {facturasPaginadas.map((factura, index) => {
                  const idFactura = factura.id_factura || (page * rowsPerPage + index);
                  const detallePagos = pagosPorFactura[idFactura];

                  return (
                    <React.Fragment key={idFactura}>
                      <TableRow hover>
                        <TableCell sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                          {factura.pto_venta}-{factura.nro_comprobante}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }}>
                          {factura.fecha_emision ? moment(factura.fecha_emision).format('DD-MM-YYYY') : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }}>
                          {factura.razon_social || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.7rem' }}>
                          {factura.id_pedido || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          ${formatNumber(factura.importe_total || 0)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver factura">
                            <IconButton
                              size="small"
                              onClick={() => verEnviarPDFenvasado(factura.id_factura, 0)}
                            >
                              <VisibilityIcon color="primary" fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ver pagos del pedido">
                            <IconButton
                              size="small"
                              onClick={() => toggleFacturaExpandida(factura)}
                              sx={{ color: '#757575' }}
                            >
                              {facturasExpandidas[idFactura] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0 }}>
                          <Box sx={{ display: facturasExpandidas[idFactura] ? 'block' : 'none' }}>
                            <Box sx={{ p: 1.5, bgcolor: '#fafafa', borderTop: '1px solid #e8e8e8' }}>
                              {detallePagos?.loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                                  <CircularProgress size={20} sx={{ color: '#757575' }} />
                                </Box>
                              ) : detallePagos?.error ? (
                                <Alert severity="error" sx={{ fontSize: '0.75rem' }}>{detallePagos.error}</Alert>
                              ) : !detallePagos?.data?.length ? (
                                <Typography variant="caption" color="textSecondary">
                                  No hay pagos registrados para el pedido {factura.id_pedido || '-'}
                                </Typography>
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.7rem' }}>Medio de Pago</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.7rem' }}>Importe</TableCell>
                                      <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.7rem' }}>Fecha</TableCell>
                                      <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.7rem' }}>Detalle</TableCell>
                                      <TableCell align="center" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.7rem' }}>Conciliado</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {detallePagos.data.map((pago, pagoIndex) => (
                                      <TableRow key={pago.id || pagoIndex}>
                                        <TableCell sx={{ fontSize: '0.7rem' }}>
                                          <Chip
                                            icon={getMedioPagoIcon(pago.nombre)}
                                            label={pago.nombre || 'Sin definir'}
                                            size="small"
                                            sx={{
                                              fontSize: '0.65rem',
                                              height: 20,
                                              bgcolor: `${getMedioPagoColor(pago.nombre)}15`,
                                              color: getMedioPagoColor(pago.nombre),
                                              '& .MuiChip-icon': { fontSize: 14 }
                                            }}
                                            variant="outlined"
                                          />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                                          ${formatNumber(pago.importe || 0)}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.7rem' }}>
                                          {pago.created_at ? moment(pago.created_at).format('DD-MM-YYYY HH:mm') : '-'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.7rem' }}>
                                          {pago.detalle || '-'}
                                        </TableCell>
                                        <TableCell align="center">
                                          <Chip
                                            label={pago.conciliado ? 'Sí' : 'No'}
                                            size="small"
                                            sx={{
                                              fontSize: '0.65rem',
                                              height: 20,
                                              bgcolor: pago.conciliado ? '#e8f5e9' : '#fafafa',
                                              color: pago.conciliado ? '#2e7d32' : '#757575'
                                            }}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    colSpan={6}
                    count={facturas.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                    labelRowsPerPage="Filas por página:"
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} de ${count}`
                    }
                    sx={{
                      '.MuiTablePagination-select': {
                        fontSize: '0.8rem'
                      },
                      '.MuiTablePagination-displayedRows': {
                        fontSize: '0.8rem'
                      }
                    }}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DialogFacturasMedioPago;
